import * as constants from '../assets/constants';
import SalePointsApi from '../api/references/salePoints';
import KlientsApi from '../api/references/klients';
import StandsApi from '../api/references/stands';
import StandCollectionsApi from '../api/references/standcollections';
import EquipmentApi from '../api/references/equipment';
import SalePointSellersApi from '../api/references/salePointSellers';

/** Внутренний ключ сущности → сегмент пути в URL (как в setActiveStory: story + '_story') */
export const OBJECT_TYPE_TO_STORY = {
    salePoint: 'salepoints',
    klient: 'klients',
    mall: 'malls',
    brand: 'stands',
    brandscollection: 'standcollections',
    equipment: 'equipment',
    staff: 'staff',
};

/** Старые сегменты URL (ссылки уже разосланы в мессенджерах). */
export const LEGACY_STORY_ALIASES = {
    brands: 'stands',
    brandscollections: 'standcollections',
};

const STORY_TO_OBJECT_TYPE = {
    ...Object.fromEntries(
        Object.entries(OBJECT_TYPE_TO_STORY).map(([k, v]) => [v, k])
    ),
    ...Object.fromEntries(
        Object.entries(LEGACY_STORY_ALIASES).map(([oldSeg, newSeg]) => [
            oldSeg,
            Object.entries(OBJECT_TYPE_TO_STORY).find(([, v]) => v === newSeg)[0],
        ])
    ),
};

/**
 * Префикс пути как у текущего приложения: /react или пусто.
 */
export function getReactPathPrefix() {
    if (typeof window === 'undefined' || !window.location || !window.location.href) {
        return '/react';
    }
    return window.location.href.includes('/react/') ? '/react' : '';
}

export function getAppOrigin() {
    return constants.DEBUG ? constants.url_stage : constants.url_prod;
}

/**
 * Id из пути /saledocs/3073025 или query ?id=3073025.
 * Хвост вроде «3073025$» из мессенджеров тоже разбирается.
 * @param {string} storySegment сегмент URL без _story, например saledocs
 * @returns {number}
 */
export function parseNumericIdFromPath(storySegment) {
    if (typeof window === 'undefined' || !window.location || !storySegment) {
        return 0;
    }
    const path = String(window.location.pathname || '').replace(/\/+$/, '');
    const parts = path.split('/').filter(Boolean);
    const idx = parts.findIndex((p) => String(p).split('?')[0] === storySegment);
    let raw = '';
    if (idx >= 0 && parts[idx + 1]) {
        raw = String(parts[idx + 1]).split('?')[0];
    }
    if (!raw) {
        const params = new URLSearchParams(window.location.search || '');
        raw = params.get('id') || '';
    }
    const id = parseInt(String(raw).replace(/[^\d].*$/, ''), 10);
    return Number.isNaN(id) || id <= 0 ? 0 : id;
}

/**
 * Прямая ссылка на объект справочника (новая вкладка).
 * @param {keyof typeof OBJECT_TYPE_TO_STORY} objectType
 * @param {number|string} id
 */
export function buildObjectUrl(objectType, id) {
    const story = OBJECT_TYPE_TO_STORY[objectType];
    if (!story) {
        return '';
    }
    const origin = getAppOrigin();
    const prefix = getReactPathPrefix();
    const q = new URLSearchParams({ id: String(id) });
    return `${origin}${prefix}/${story}?${q.toString()}`;
}

export function openObjectInNewTab(objectType, id) {
    const url = buildObjectUrl(objectType, id);
    if (!url) {
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Сегмент story из URL (без _story) → внутренний тип объекта.
 * @param {string} storySegment
 */
export function resolveStoryToObjectType(storySegment) {
    if (!storySegment || typeof storySegment !== 'string') {
        return null;
    }
    const clean = storySegment.split('?')[0].replace(/\/+$/, '').trim();
    return STORY_TO_OBJECT_TYPE[clean] || null;
}

export function normalizeGetOnePayload(data) {
    if (!data || typeof data !== 'object') {
        return null;
    }
    if (data.id != null && data.id !== '') {
        return data;
    }
    if (data.result && data.result.id != null) {
        return data.result;
    }
    if (data.data && data.data.id != null) {
        return data.data;
    }
    if (data.item && data.item.id != null) {
        return data.item;
    }
    return null;
}

const MESSAGE_TYPES = {
    salePoint: 'setCurrentSalePointRef',
    klient: 'setCurrentKlient',
    mall: 'setCurrentMall',
    brand: 'setCurrentBrand',
    brandscollection: 'setCurrentBrandCollection',
    equipment: 'setCurrentEquipment',
    staff: 'setCurrentStaff',
};

function enrichPayload(objectType, value) {
    if (objectType === 'mall' && value && (value.isMall === undefined || value.isMall === null)) {
        value.isMall = 1;
    }
    return value;
}

async function fetchPayload(objectType, id) {
    const res = await (() => {
        switch (objectType) {
            case 'salePoint':
            case 'mall':
                return SalePointsApi.getOne(id);
            case 'klient':
                return KlientsApi.getOne(id);
            case 'brand':
                return StandsApi.getOne(id);
            case 'brandscollection':
                return StandCollectionsApi.getOne(id);
            case 'equipment':
                return EquipmentApi.getOne(id);
            case 'staff':
                return SalePointSellersApi.getOne(id);
            default:
                return Promise.reject(new Error('unknown type'));
        }
    })();
    return normalizeGetOnePayload(res.data);
}

/**
 * Загружает объект по API и шлёт postMessage, как при открытии из списка.
 * @param {keyof typeof OBJECT_TYPE_TO_STORY} objectType
 * @param {number} id
 * @returns {Promise<boolean>} успех
 */
export async function fetchReferenceObjectAndOpen(objectType, id) {
    const messageType = MESSAGE_TYPES[objectType];
    if (!messageType || !id || id <= 0) {
        return false;
    }
    try {
        const value = await fetchPayload(objectType, id);
        if (!value || value.id == null) {
            window.postMessage({
                type: 'error',
                text: 'Объект не найден',
                timeout: 5000,
            });
            return false;
        }
        enrichPayload(objectType, value);
        window.postMessage({ type: messageType, value });
        return true;
    } catch (e) {
        window.postMessage({
            type: 'error',
            text: 'Объект не найден',
            timeout: 5000,
        });
        return false;
    }
}
